import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Fuel, ExternalLink } from "lucide-react";

export default function FuelPage() {
  const [airnav, setAirnav] = useState(null);

  useEffect(() => {
    const load = () => api.get("/airnav-fuel").then((r) => setAirnav(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 15 * 60 * 1000); // refresh every 15 min
    return () => clearInterval(id);
  }, []);

  const s = airnav?.summary || {};
  const fbos = airnav?.fbos || [];

  return (
    <div data-testid="fuel-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Fuel Prices · AirNav Live</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-950 mt-3">Tanks topped. Wallets light.</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Live prices pulled directly from AirNav.com for KSME. Self-serve is available 24/7;
        full-serve is available during attended hours.
      </p>

      {/* Big price grid: 100LL / Jet A × FS / SS */}
      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <PriceCard fuel="100LL" fs={s.avgas_100ll_fs} ss={s.avgas_100ll_ss} accent />
        <PriceCard fuel="Jet A" fs={s.jet_a_fs} ss={s.jet_a_ss} />
      </div>

      {/* FBO listings */}
      {fbos.length > 0 && (
        <section className="mt-16">
          <div className="text-xs font-mono uppercase tracking-widest text-sky-600 mb-6">Fuel Providers at KSME</div>
          <div className="grid md:grid-cols-2 gap-6">
            {fbos.map((fbo, i) => (
              <div key={i} data-testid={`fbo-${i}`} className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-xl font-semibold text-slate-950">{fbo.fbo_name}</div>
                    {fbo.brand && <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">Brand · {fbo.brand}</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {(fbo.brand || "").toLowerCase().includes("titan") && (
                      <img
                        src="https://customer-assets.emergentagent.com/job_skyport-info/artifacts/9q4b7n0n_TITAN%20LOGO%20NEW.HORIZONTAL.jpg"
                        alt="Titan Aviation Fuels"
                        className="h-10 w-auto object-contain"
                      />
                    )}
                    {fbo.guaranteed && (
                      <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-600 text-emerald-700 px-2 py-1">Guaranteed</span>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2 text-sm font-mono-data">
                  <div className="text-slate-500 text-xs uppercase tracking-widest"></div>
                  <div className="text-slate-500 text-xs uppercase tracking-widest text-center">Full Serve</div>
                  <div className="text-slate-500 text-xs uppercase tracking-widest text-center">Self Serve</div>
                  {fbo.fuels.map((f) => (
                    <React.Fragment key={f.name}>
                      <div className="font-semibold text-slate-950">{f.name}</div>
                      <div className="text-center">{f.full_serve != null ? `$${f.full_serve.toFixed(2)}` : "—"}</div>
                      <div className="text-center">{f.self_serve != null ? `$${f.self_serve.toFixed(2)}` : "—"}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Discounts */}
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <div className="bg-slate-950 text-white p-8">
          <div className="text-xs font-mono uppercase tracking-widest text-sky-300">Volume Discounts</div>
          <p className="mt-3 text-sm text-slate-300">Per-gallon discount applied automatically at the pump based on single-fill volume.</p>
          <dl className="mt-6 font-mono-data text-sm">
            {[
              ["100 – 199 gal", "$0.10 off / gal"],
              ["200 – 299 gal", "$0.20 off / gal"],
              ["300+ gal",      "$0.30 off / gal"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between border-b border-white/10 py-3">
                <dt className="text-slate-300">{k}</dt>
                <dd className="text-white font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="bg-slate-50 border border-slate-200 p-8">
          <div className="text-xs font-mono uppercase tracking-widest text-sky-600">Source & Updates</div>
          <p className="mt-3 text-slate-700 text-sm leading-relaxed">
            Prices on this page are pulled live from AirNav.com and refreshed automatically every hour.
            Contract fuel programs (Titan, AVCard, Multi Service, U.S. Gov AIR Card) are accepted at KSME.
          </p>
          <a
            href={airnav?.url || "https://www.airnav.com/airport/KSME"}
            target="_blank" rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-950 hover:text-sky-600"
            data-testid="airnav-link"
          >
            View on AirNav.com <ExternalLink className="h-4 w-4" />
          </a>
          {airnav?.fetched_at && (
            <p className="mt-4 text-xs font-mono uppercase tracking-widest text-slate-500">
              Last updated {new Date(airnav.fetched_at).toLocaleString()}
              {!airnav.live && airnav.note && <> · <span className="text-amber-700 normal-case">{airnav.note}</span></>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceCard({ fuel, fs, ss, accent }) {
  return (
    <div className={`p-8 border ${accent ? "bg-slate-950 text-white border-slate-950" : "bg-white border-slate-200"}`}>
      <div className="flex items-center gap-2">
        <Fuel className={`h-5 w-5 ${accent ? "text-sky-300" : "text-sky-600"}`} />
        <div className={`text-xs font-mono uppercase tracking-widest ${accent ? "text-sky-300" : "text-sky-600"}`}>{fuel}</div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <div className={`text-[11px] font-mono uppercase tracking-widest ${accent ? "text-slate-400" : "text-slate-500"}`}>Full Serve</div>
          <div className="font-display font-bold text-4xl sm:text-5xl mt-1">${fs?.toFixed(2) ?? "—"}</div>
        </div>
        <div>
          <div className={`text-[11px] font-mono uppercase tracking-widest ${accent ? "text-slate-400" : "text-slate-500"}`}>Self Serve</div>
          <div className="font-display font-bold text-4xl sm:text-5xl mt-1">${ss?.toFixed(2) ?? "—"}</div>
        </div>
      </div>
      <div className={`mt-3 text-xs ${accent ? "text-slate-400" : "text-slate-500"}`}>per gallon · USD</div>
    </div>
  );
}
